import styled from '@emotion/styled';
import { useEffect, useRef, memo } from 'react';

import { theme } from '../../../theme/theme';

export interface StreamVideoViewProps {
  stream: MediaStream | null;
  includeAudio: boolean;
  pointerEvents?: 'auto' | 'none';
}

/**
 * Simple component that renders a video element with a provided MediaStream.
 * Used when the stream is managed externally (e.g., shared between inline and fullscreen views).
 */
function StreamVideoView(props: StreamVideoViewProps) {
  const { stream, includeAudio } = props;
  const videoRef = useRef<HTMLVideoElement>(null);

  // Attach stream to video element
  useEffect(() => {
    const videoElement = videoRef.current;

    if (stream && videoElement) {
      videoElement.srcObject = stream;
    }

    return () => {
      if (videoElement) {
        // Don't null out srcObject - the stream is managed externally
        // and may be used by other components
      }
    };
  }, [stream]);

  // Control audio muting
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = !includeAudio;
    }
  }, [includeAudio]);

  return (
    <S.Container style={{ pointerEvents: props.pointerEvents }}>
      <S.Video ref={videoRef} autoPlay playsInline muted={!includeAudio} />
    </S.Container>
  );
}

export const MemoizedStreamVideoView = memo(StreamVideoView);

function S() {}
StreamVideoView.S = S;

S.Container = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  background-color: ${theme.color.black};
  display: flex;
  justify-content: center;
  align-items: center;
`;

S.Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: ${theme.color.black};
`;
